import { FilterOptionsState } from '@mui/base';
import { formatTranslation, unsafeTranslationKey } from 'intl';
import { CommonTranslationKeys } from 'intl/keys';
import {
  AutocompleteChangeReason,
  createFilterOptions,
} from '@mui/material/Autocomplete';
import { CreateCompanyContext } from 'components/dataCreationForms/company/types';
import {
  WithCreateCompany,
  useCreateCompany,
} from 'components/dataCreationForms/company/withCreateCompany';
import { CreateContractContext } from 'components/dataCreationForms/contract/types';
import {
  WithCreateContract,
  useCreateContract,
} from 'components/dataCreationForms/contract/withCreateContract';
import {
  CreateLevelContext,
  OpenCreateLevelDialog,
  PreselectedMapContext,
} from 'components/dataCreationForms/level/types';
import {
  WithCreateLevel,
  useCreateLevel,
} from 'components/dataCreationForms/level/withCreateLevel';
import { CreateOrganizationContext } from 'components/dataCreationForms/organization/types';
import {
  WithCreateOrganization,
  useCreateOrganization,
} from 'components/dataCreationForms/organization/withCreateOrganization';
import { CreateSiteContext } from 'components/dataCreationForms/site/types';
import {
  WithCreateSite,
  useCreateSite,
} from 'components/dataCreationForms/site/withCreateSite';
import { OpenCreateDialog } from 'components/dataCreationForms/types';
import {
  CreateUserContext,
  OpenCreateUserDialog,
} from 'components/dataCreationForms/user/types';
import {
  WithCreateUser,
  useCreateUser,
} from 'components/dataCreationForms/user/withCreateUser';
import {
  FieldValueFormContext,
  WithFieldValueForm,
  useFieldValueForm,
} from 'components/dataCreationForms/withFieldValueForm';
import { WithFieldValuesForm } from 'components/dataCreationForms/withFieldValuesForm';
import { useInputForm } from 'components/dataCreationForms/withInputForm';
import {
  withCustomizedField,
} from 'components/dataProviders/withCustomizedField';
import { ErrorPresentation } from 'helpers/validators';
import { useCanCreate } from 'hooks/permissions/useCanCreate';
import { ReactElement, ReactNode, SyntheticEvent, useMemo } from 'react';
import { IntlShape } from 'react-intl';
import {
  EditableFieldValuesEntityName,
  EditableStandardEntityName,
  editableFieldValuesEntityNames,
} from 'shared/domain/fieldValue/fields';
import { LabelledEntity } from 'shared/types/commonView';
import { MemoCreateCompanyWizardDialog } from 'views/companies/wizard/create/dialog';
import { MemoCreateContractWizardDialog } from 'views/contracts/wizard/create/dialog';
import { FieldValueDialog } from 'views/fieldsSettings/wizard/dialog';
import { MemoCreateLevelWizardDialog } from 'views/level/wizard/create/dialog';
import { MemoCreateOrganizationWizardDialog } from 'views/organization/wizard/create/dialog';
import { MemoCreateSiteWizardDialog } from 'views/site/wizard/create/dialog';
import { MemoCreateUserWizardDialog } from 'views/users/wizard/create/dialog';
import { SingleChoiceValue } from './singleChoice/types';
import { FormValuesGetter, FreeSoloMultiAutocompleteValue } from './types';

/** Stable dialog elements — avoid new element refs on every getFreeSoloProps call. */
const freeSoloFieldValueDialog = <FieldValueDialog instantSubmit={true} />;
const freeSoloCreateCompanyDialog = <MemoCreateCompanyWizardDialog />;
const freeSoloCreateSiteDialog = <MemoCreateSiteWizardDialog />;
const freeSoloCreateLevelDialog = <MemoCreateLevelWizardDialog />;
const freeSoloCreateUserDialog = <MemoCreateUserWizardDialog />;
const freeSoloCreateContractDialog = <MemoCreateContractWizardDialog />;
const freeSoloCreateOrganizationDialog = <MemoCreateOrganizationWizardDialog />;

export function getHelperTextFromError(
  error: any,
  intl: IntlShape
): string | undefined {
  if (!error) {
    return '';
  }
  if (error) {
    if (typeof error === 'string') {
      return error;
    } else {
      return formatTranslation(intl, unsafeTranslationKey(error.helperText?.id) , error?.values
      );
    }
  }
}

export function getLocalizedLabel(
  intl: IntlShape,
  localisedLabel?: string,
  labelId?: string
): string {
  return (
    localisedLabel ||
    (labelId ? formatTranslation(intl, unsafeTranslationKey(labelId) ) : 'Unknown label')
  );
}

export function unifiedGetOptionLabel(option: any): string {
  if (typeof option === 'string') {
    return option;
  } else {
    return option?.label ?? option?.displayName ?? '';
  }
}

export function unifiedGetOptionKey(option: any): string {
  if (typeof option === 'string') {
    return option;
  } else {
    return option?._id ?? option?.id ?? `randomKey-${Math.random()}`;
  }
}

export function isOptionEqualToValue(
  option: string | LabelledEntity,
  value: LabelledEntity
): boolean {
  if (typeof option === 'string' || typeof value === 'string') {
    return false;
  }
  if (option?._id === value?._id) {
    return true;
  }
  return false;
}

export function getAutocompleteDisplayedHelperText(
  intl: IntlShape,
  helperText?: string,
  error?: any
): string | undefined {
  return (
    helperText ||
    getHelperTextFromError(error, intl) ||
    // TODO MUI5 pomysł jest dobry, ale wyświetla się to od razu przy podlokalizacji na dialogu tworzenia issue - trzeba to przemyśleć
    // (optionsLength === 0 &&
    //   formatTranslation(intl, CommonTranslationKeys.autocompleteNoOptions)) ||
    undefined
  );
}

export {
  getAutocompleteRenderInput,
  type GetAutocompleteRenderInputProps,
} from './autocompleteRenderInput';
type FieldValueProviderComponent = (props: {
  children?: ReactNode;
}) => ReactElement;

const fieldValueProviderComponents = new Map<
  EditableFieldValuesEntityName,
  FieldValueProviderComponent
>();

/**
 * One component type per field for the app lifetime. Creating it per call
 * gave React a new element type on every render, remounting the forms each
 * time the field re-rendered.
 *
 * Order: withCustomizedField -> WithFieldValuesForm -> WithFieldValueForm.
 */
function getFieldValueProviderComponent(
  entityName: EditableFieldValuesEntityName
): FieldValueProviderComponent {
  const cached = fieldValueProviderComponents.get(entityName);
  if (cached) {
    return cached;
  }

  const ComponentWithForms = (props: any): ReactElement => (
    <WithFieldValuesForm instantSubmit={true}>
      <WithFieldValueForm {...props} />
    </WithFieldValuesForm>
  );

  const component = withCustomizedField(
    ComponentWithForms,
    entityName
  ) as FieldValueProviderComponent;
  fieldValueProviderComponents.set(entityName, component);
  return component;
}

export function getFreeSoloProps(
  entityName:
    | string
    | EditableFieldValuesEntityName
    | EditableStandardEntityName
):
  | {
      Provider: ({ children }) => ReactElement;
      useProvider: () =>
        | FieldValueFormContext
        | CreateCompanyContext
        | CreateSiteContext
        | CreateLevelContext
        | CreateUserContext
        | CreateContractContext
        | CreateOrganizationContext;
      DialogComponent: ReactElement;
    }
  | undefined {
  const canUserCreateOption = useCanCreate(entityName);
  if (canUserCreateOption) {
    if (
      editableFieldValuesEntityNames.find(
        (customizableField) => customizableField === entityName
      )
    ) {
      const FieldValueProviderComponent = getFieldValueProviderComponent(
        entityName as EditableFieldValuesEntityName
      );

      return {
        Provider: FieldValueProviderComponent,
        useProvider: useFieldValueForm,
        DialogComponent: freeSoloFieldValueDialog,
      };
    } else if (entityName === EditableStandardEntityName.company) {
      return {
        Provider: WithCreateCompany,
        useProvider: useCreateCompany,
        DialogComponent: freeSoloCreateCompanyDialog,
      };
    } else if (entityName === EditableStandardEntityName.location) {
      return {
        Provider: WithCreateSite,
        useProvider: useCreateSite,
        DialogComponent: freeSoloCreateSiteDialog,
      };
    } else if (entityName === EditableStandardEntityName.level) {
      return {
        Provider: WithCreateLevel,
        useProvider: useCreateLevel,
        DialogComponent: freeSoloCreateLevelDialog,
      };
    } else if (entityName === EditableStandardEntityName.user) {
      return {
        Provider: WithCreateUser,
        useProvider: useCreateUser,
        DialogComponent: freeSoloCreateUserDialog,
      };
    } else if (entityName === EditableStandardEntityName.contract) {
      return {
        Provider: WithCreateContract,
        useProvider: useCreateContract,
        DialogComponent: freeSoloCreateContractDialog,
      };
    } else if (entityName === EditableStandardEntityName.organization) {
      return {
        Provider: WithCreateOrganization,
        useProvider: useCreateOrganization,
        DialogComponent: freeSoloCreateOrganizationDialog,
      };
    }
  }
}

function getAddValueLabel(inputValue: string, intl: IntlShape): string {
  return `${formatTranslation(intl, CommonTranslationKeys.bottomNavCreate,)} "${inputValue}"`;
}

function getOptionFilterText(
  option: FreeSoloMultiAutocompleteValue
): string {
  return `${option.label} ${option.group ?? ''}`;
}

export function multipleFreeSoloFilterOptions(
  options: FreeSoloMultiAutocompleteValue[],
  params: FilterOptionsState<
    FreeSoloMultiAutocompleteValue & { inputValue?: string | undefined }
  >,
  intl: IntlShape
): FreeSoloMultiAutocompleteValue[] {
  const filter = createFilterOptions<FreeSoloMultiAutocompleteValue>({
    stringify: getOptionFilterText,
  });
  const filtered = filter(options, params);

  if (params.inputValue !== '') {
    filtered.push({
      _id: '',
      inputValue: params.inputValue,
      label: getAddValueLabel(params.inputValue, intl),
    });
  }

  return filtered;
}

export function singleFreeSoloFilterOptions(
  options: LabelledEntity[],
  params: FilterOptionsState<
    LabelledEntity & { inputValue?: string | undefined }
  >,
  intl: IntlShape
): (LabelledEntity & {
    inputValue?: string | undefined;
    isCreationProposal?: boolean;
  })[] {
  const filter = createFilterOptions<
    LabelledEntity & { inputValue?: string; isCreationProposal?: boolean }
  >();

  const filtered = filter(options, params);

  const { inputValue } = params;
  // Suggest the creation of a new value
  if (inputValue && typeof inputValue === 'string') {
    const isExisting = options.some(
      (option) => inputValue === option.label
    );
    if (!isExisting) {
      filtered.push({
        _id: '',
        inputValue,
        label: getAddValueLabel(inputValue, intl),
        isCreationProposal: true,
      });
    }
  }

  return filtered;
}

export interface GetOnMultipleFreeSoloChangeHandlerAdditionalParams {
  siteId?: string;
  siteIds?: string[];
  preselectedMap?: PreselectedMapContext;
}

export function getPreselectedMapFromForm(
  formValuesGetter?: FormValuesGetter
): Pick<GetOnMultipleFreeSoloChangeHandlerAdditionalParams, 'preselectedMap'> {
  if (!formValuesGetter) {
    return {};
  }

  const { preselectedMap } = formValuesGetter();

  return preselectedMap ? { preselectedMap } : {};
}

export function getOnMultipleFreeSoloChangeHandler(
  openDialog:
    | OpenCreateDialog
    | OpenCreateLevelDialog
    | OpenCreateUserDialog,
  handleChange: (_: any, value: FreeSoloMultiAutocompleteValue[]) => void,
  additionalParams: GetOnMultipleFreeSoloChangeHandlerAdditionalParams
) {
  return (
    event: SyntheticEvent<Element, Event>,
    newValues: FreeSoloMultiAutocompleteValue[]
  ): void => {
    const newlyCreatedOption = newValues.find((item) => !item?._id);
    if (newlyCreatedOption) {
      const newLabel =
        typeof newlyCreatedOption === 'string'
          ? newlyCreatedOption
          : newlyCreatedOption.inputValue;
      openDialog({
        freeSoloLabel: newLabel || '',
        siteId: additionalParams.siteId || '',
        siteIds: additionalParams.siteIds || [],
        preselectedMap: additionalParams.preselectedMap,
      });
      event.preventDefault();
      event.stopPropagation();
    } else {
      handleChange(event, newValues);
    }
  };
}

export function getOnSingleFreeSoloChangeHandler(
  openDialog: OpenCreateDialog | OpenCreateLevelDialog,
  handleChange: (_: any, value?: SingleChoiceValue) => void,
  setValue: (value: any) => void,
  { siteId }: { siteId?: string }
) {
  return (
    event: SyntheticEvent<Element, Event>,
    newValue: SingleChoiceValue & { inputValue?: string },
    reason: AutocompleteChangeReason
  ): void => {
    if (typeof newValue === 'string') {
      // if written directly by user and not selected from dropdown
      // reason is then 'createReason'
      setValue(undefined);
      return openDialog({ freeSoloLabel: newValue, siteId: siteId || '' });
    }
    if (newValue?.inputValue) {
      // if selected from dropdown as "Add something" option
      // reason is then 'selectOption'
      setValue(undefined);
      openDialog({
        freeSoloLabel: newValue.inputValue,
        siteId: siteId || '',
      });
      return;
    }
    handleChange(event, newValue);
  };
}

/** Site id from already-resolved form values (no getter / hook). */
export function getSiteIdFromValues(values: {
  site?: LabelledEntity;
  sites?: LabelledEntity[];
}): string {
  if (values.site?._id) {
    return values.site._id;
  }
  const sites = values.sites;
  if (Array.isArray(sites) && sites.length > 0) {
    return sites[0]._id;
  }
  return '';
}

export function getSiteIdFromForm(formValuesGetter?: FormValuesGetter): string {
  const values = formValuesGetter
    ? formValuesGetter().values
    : useInputForm().values;

  return getSiteIdFromValues(values as { site?: LabelledEntity; sites?: LabelledEntity[] });
}

/** True when the form shape includes a location field (filled or empty). */
export function formHasSiteField(values: object): boolean {
  return (
    Object.prototype.hasOwnProperty.call(values, 'site') ||
    Object.prototype.hasOwnProperty.call(values, 'sites')
  );
}

interface FreeSoloAdditionalParams { siteId?: string; siteIds?: string[] }

export function getFreeSoloDialogAdditionalParams(
  siteId?: string
): FreeSoloAdditionalParams {
  return {
    siteId,
    siteIds: siteId ? [siteId] : undefined,
  };
}

export type PopupControlStrategy = (selectedCount: number) => boolean;

export const closeAfterFirstThenStayOpenStrategy = (
  selectedCount: number
): boolean => selectedCount >= 1;

export const getDisableCloseOnSelect = (
  popupControlStrategy: PopupControlStrategy,
  selectedCount: number
): boolean => {
  return popupControlStrategy(selectedCount);
};
